import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import { remeasureMonacoFontsWhenReady } from '../../utils/monacoFonts';
import Editor from '@monaco-editor/react';
import { useThemeStore } from '../../store/themeStore';
import {
  registerMonacoThemes,
  resolveMonacoTheme,
  MONACO_EDITOR_COLORS,
} from '../../utils/monacoThemes';

const LINE_HEIGHT = 18;
const VERTICAL_PADDING = 16;

function useIsDark() {
  const theme = useThemeStore((s) => s.theme);
  return theme === 'dark'
    || (theme === 'system'
      && typeof window !== 'undefined'
      && window.matchMedia('(prefers-color-scheme: dark)').matches);
}

function toMonacoLanguage(language) {
  switch (language) {
    case 'json': return 'json';
    case 'javascript': return 'javascript';
    case 'xml': return 'xml';
    case 'html': return 'html';
    default: return 'plaintext';
  }
}

export default function MonacoReadOnly({
  value,
  language = 'json',
  maxHeight = 180,
  className = '',
}) {
  const isDark = useIsDark();
  const monacoTheme = resolveMonacoTheme(isDark);
  const monacoLanguage = toMonacoLanguage(language);
  const colors = isDark ? MONACO_EDITOR_COLORS.dark : MONACO_EDITOR_COLORS.light;
  const wrapperRef = useRef(null);

  // Redirect wheel events to the nearest scrollable ancestor so Monaco code
  // blocks don't trap scroll when the user is reading through the chat.
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onWheel = (e) => {
      let parent = el.parentElement;
      while (parent) {
        const { overflow, overflowY } = getComputedStyle(parent);
        if (/auto|scroll/.test(overflow + overflowY) && parent.scrollHeight > parent.clientHeight) {
          parent.scrollBy({ top: e.deltaY, left: e.deltaX });
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        parent = parent.parentElement;
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const height = useMemo(() => {
    const lines = Math.max(1, String(value || '').split('\n').length);
    return Math.min(maxHeight, Math.max(48, lines * LINE_HEIGHT + VERTICAL_PADDING));
  }, [value, maxHeight]);

  const handleMount = useCallback((editor, monaco) => {
    // Metrics are measured at creation; JetBrains Mono may not have loaded yet.
    remeasureMonacoFontsWhenReady(monaco);

    // Tool blocks mount Monaco during streaming — don't steal focus from the chat input.
    requestAnimationFrame(() => {
      editor.getDomNode()?.blur();
      const chatInput = document.querySelector('.ai-chat-input');
      if (chatInput) chatInput.focus();
    });
  }, []);

  const options = useMemo(() => ({
    readOnly: true,
    domReadOnly: true,
    readOnlyMessage: { value: '' },
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    lineNumbers: 'off',
    folding: false,
    glyphMargin: false,
    lineDecorationsWidth: 0,
    lineNumbersMinChars: 0,
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    overviewRulerBorder: false,
    renderLineHighlight: 'none',
    scrollbar: {
      vertical: 'auto',
      horizontal: 'auto',
      verticalScrollbarSize: 6,
      horizontalScrollbarSize: 6,
      useShadows: false,
    },
    wordWrap: 'on',
    fontSize: 11,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    padding: { top: 4, bottom: 4 },
    automaticLayout: true,
    contextmenu: false,
    selectionHighlight: false,
    occurrencesHighlight: 'off',
    // Don't flag ambiguous/invisible/non-ASCII chars (avoids the "ambiguous unicode characters" banner)
    unicodeHighlight: {
      ambiguousCharacters: false,
      invisibleCharacters: false,
      nonBasicASCII: false,
    },
  }), []);

  return (
    <div
      ref={wrapperRef}
      className={className}
      style={{
        maxHeight,
        overflow: 'hidden',
        backgroundColor: colors.background,
      }}
    >
      <Editor
        value={value || ''}
        language={monacoLanguage}
        theme={monacoTheme}
        beforeMount={registerMonacoThemes}
        onMount={handleMount}
        height={height}
        options={options}
        loading={
          <div style={{ height, backgroundColor: colors.background }} />
        }
      />
    </div>
  );
}
